import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { angleToRadian } from '@src/slide/infrastructure/module/independent/graphic';
import has from 'lodash/has';
import { COORDINATE } from '@editor/src/js/constants';
import { setModeLeftOffsetFrameWithShiftKey, setModeTopOffsetFrameWithShiftKey } from '@editor/src/page/index/components/canvas/utils';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';

export interface ILines {
    left: number | undefined;
    top: number | undefined;
}

export interface IOffsetFrame {
    left: number;
    top: number;
    width: number;
    height: number;
}

const LINES_MODE_INDEX = 3;
const LINES_DISTSNCE_INDEX = 0;
const NO_ROTATE = 0;

export default class SnaplineLogic {
    /**
    * 是否有旋转
    * @param rotate 旋转角度
    * @returns 是否旋转
    */
    public static hasRotate(rotate: number | undefined): boolean {
        if (rotate && expect(rotate).isNumber()) {
            return rotate % 360 !== 0;
        }
        return false;
    }

    /**
    * 根据要吸附的位置计算和修改offsetFrame
    */
    public static caculateFrame(adsorptionParams): void {
        const { lines, outer } = adsorptionParams;
        if (lines?.length > 0) {
            const coordinate = this.adsorptionByLines(lines, outer.width, outer.height);
            adsorptionParams.coordinate = coordinate;
            if (has(coordinate, 'left') && coordinate.left) {
                this.switchModeLeft(adsorptionParams);
            }
            if (has(coordinate, 'top') && coordinate.top) {
                this.switchModeTop(adsorptionParams);
            }
        }
    }

    /**
     * 计算多选时 根据要吸附的位置计算和修改offsetFrame
     */
    public static caculateFrame4m(e, mode, lines, outer, original, offsetFrame, multiRect) {
        if (lines && lines.length > 0) {
            const coordinate = this.adsorptionByLines(lines, multiRect.width, multiRect.height);
            const scale = original.height / original.width;
            if (has(coordinate, 'left') && coordinate.left) {
                // 封装成函数以降低圈复杂度
                this.switchModeLeft4m(e, outer, mode, coordinate, original, offsetFrame, scale, multiRect);
            }
            if (has(coordinate, 'top') && coordinate.top) {
                this.switchModeTop4m(e, outer, mode, coordinate, original, offsetFrame, scale, multiRect);
            }
        }
    }

    // 吸附上下对齐线step1 区分上对齐线和下对齐线，分别计算
    /**
     * 吸附上下对齐线
     * @param adsorptionParams
     */
    private static switchModeTop(adsorptionParams): void {
        const { coordinate, e, mode, original, offsetFrame } = adsorptionParams;
        switch (mode) {
            case MODES.MOVE:
                offsetFrame.top = coordinate.top - original.top;
                break;
            case MODES.TOP:
            case MODES.TOP_LEFT:
            case MODES.TOP_RIGHT:
                this.adsorptTopBottom(MODES.TOP, adsorptionParams);
                break;
            case MODES.BOTTOM:
            case MODES.BOTTOM_LEFT:
            case MODES.BOTTOM_RIGHT:
                this.adsorptTopBottom(MODES.BOTTOM, adsorptionParams);
                break;
            default:
                break;
        }

        setModeTopOffsetFrameWithShiftKey({
            shiftKey: e?.shiftKey,
            mode,
            offsetFrame,
            scale: original.height / original.width,
        });
    }

    /**
    * 调整吸附到上/下对齐线
    * @param modes 拉伸的点
    * @param adsorptionParams
    */
    private static adsorptTopBottom(modes: string, adsorptionParams) {
        const { rotate, nextSize } = adsorptionParams;
        // 吸附上下对齐线step2 区分旋转和非旋转，分别计算
        if (this.hasRotate(rotate)) {
            this.adsorptTopBottomRotate(modes, adsorptionParams);
        } else {
            // 吸附上下对齐线step3-2 分别计算未旋转时吸附到上下对齐线需要调整的位置
            if (this.hasRotate(this.getRotateForGroupChildren(nextSize))) {
                // 组合内的元素相对组合无旋转，但组合有旋转
                this.adsorptTopBottomNoRotateRelativeGroup(adsorptionParams);
            } else {
                const { coordinate, offsetFrame, original } = adsorptionParams;
                const topchange = coordinate.top  - original.top - offsetFrame.top;
                this.calOffsetFrameHeight(offsetFrame, -topchange);
            }
        }
    }

    // 吸附上下对齐线step3-1 分别计算旋转时吸附到上下对齐线需要调整的位置
    /**
    * 调整有旋转的元素吸附到上/下对齐线
    * @param modes 拉伸的点
    * @param adsorptionParams
    */
    private static adsorptTopBottomRotate(modes: string, adsorptionParams) {
        const { coordinate, offsetFrame, outer, nextSize, elementModel } = adsorptionParams;
        // 能包裹旋转的形状的形状距离上/下对齐线的距离
        const topchange = coordinate.top  - outer.top;
        const rotate = this.getRotateRelativeSlide(nextSize, elementModel?.props?.style?.rotate);
        if (rotate % 90 === 0) {
            // 90°的倍数要单独处理，防止拉伸某一方向出现除数为0的情况。
            this.adsorptTopBottomRotate90(modes, rotate, topchange, offsetFrame);
        } else {
            const rad = angleToRadian(rotate);
            if (offsetFrame.height === 0) {
            // 表明旋转后拉伸的是宽度
                let width = topchange / Math.abs(Math.sin(rad));
                const leftchange = topchange / Math.tan(rad);
                if (modes === MODES.BOTTOM) {
                    width *= -1;
                }
                this.calOffsetFrameWidthRotate(offsetFrame, width, leftchange, rad);
            } else {
                let height = topchange / Math.abs(Math.cos(rad));
                if (modes === MODES.BOTTOM) {
                    height *= -1;
                }
                this.calOffsetFrameHeightRotate(offsetFrame, height, topchange, rad);
            }
        }
    }

    /**
    * 处理当元素旋转90°的倍数时，吸附上下对齐线需要调整的位置
    * @param modes 拉伸的点
    * @param rotate 旋转的角度
    * @param topchange 距离对齐线的距离
    * @param offsetFrame 元素样式的变更信息
    */
    private static adsorptTopBottomRotate90(
        modes: string, rotate: number, topchange: number,
        offsetFrame: IOffsetFrame,
    ) {
        if (rotate === 180) {
            this.calOffsetFrameHeight(offsetFrame, -topchange);
        } else {
            // 当rotate为90或者270时，改变的是形状的宽度
            if (modes === MODES.TOP) {
                offsetFrame.width -= topchange;
                offsetFrame.left += topchange / 2;
            } else {
                offsetFrame.width += topchange;
                offsetFrame.left -= topchange / 2;
            }
            offsetFrame.top += topchange / 2;
        }
    }

    /**
    * 吸附上下对齐线, 处理组合中的元素，自身无旋转，但parent有旋转，
    * @param adsorptionParams
    */
    private static adsorptTopBottomNoRotateRelativeGroup(adsorptionParams) {
        const { coordinate, offsetFrame, outer, nextSize } = adsorptionParams;
        const { rotate } = nextSize;
        const rad = angleToRadian(rotate);
        const topchange = coordinate.top  - outer.top;
        if (offsetFrame.height === 0) {
            const width = rotate === 180 ? topchange : topchange / Math.sin(rad);
            this.calOffsetFrameWidth(offsetFrame, width);
        } else {
            const height = (rotate === 90 || rotate === 270) ? topchange : topchange / Math.cos(rad);
            this.calOffsetFrameHeight(offsetFrame, -height);
        }
    }

    // 吸附左右对齐线step1 区分左对齐线和右对齐线，分别计算
    /**
    * 吸附左右对齐线
    * @param adsorptionParams
    */
    private static switchModeLeft(adsorptionParams) {
        const { coordinate, e, mode, offsetFrame, original } = adsorptionParams;
        switch (mode) {
            case MODES.MOVE:
                offsetFrame.left = coordinate.left  - original.left;
                break;
            case MODES.LEFT:
            case MODES.TOP_LEFT:
            case MODES.BOTTOM_LEFT:
                this.adsorptLeftRight(MODES.LEFT, adsorptionParams);
                break;
            case MODES.RIGHT:
            case MODES.TOP_RIGHT:
            case MODES.BOTTOM_RIGHT:
                this.adsorptLeftRight(MODES.RIGHT, adsorptionParams);
                break;
            default:
                break;
        }

        setModeLeftOffsetFrameWithShiftKey({
            shiftKey: e?.shiftKey,
            mode,
            offsetFrame,
            scale: original.height / original.width,
        });
    }

    // 吸附左右对齐线step2 区分旋转和非旋转，分别计算
    /**
    * 调整吸附到左/右对齐线
    * @param modes 拉伸的点
    * @param adsorptionParams
    */
    private static adsorptLeftRight(modes: string, adsorptionParams) {
        const { rotate, nextSize } = adsorptionParams;
        if (this.hasRotate(rotate)) {
            this.adsorptLeftRightRotate(modes, adsorptionParams);
        } else {
            // 吸附左右对齐线step3-2 分别计算未旋转时吸附到左右对齐线需要调整的位置
            if (this.hasRotate(this.getRotateForGroupChildren(nextSize))) {
                // 组合内的元素相对组合无旋转，但组合有旋转
                this.adsorptLeftRightNoRotateRelativeGroup(adsorptionParams);
            } else {
                const { coordinate, offsetFrame, original } = adsorptionParams;
                const leftchange = coordinate.left  - original.left - offsetFrame.left;
                this.calOffsetFrameWidth(offsetFrame, leftchange);
            }
        }
    }

    // 吸附左右对齐线step3-1 分别计算旋转时吸附到左右对齐线需要调整的位置
    /**
    * 调整有旋转的元素吸附到左/右对齐线
    * @param modes 拉伸的点
    * @param adsorptionParams
    */
    private static adsorptLeftRightRotate(modes: string, adsorptionParams) {
        const { coordinate, offsetFrame, outer, nextSize, elementModel } = adsorptionParams;
        // 能包裹旋转的形状的形状距离左/右对齐线的距离
        const leftchange = coordinate.left  - outer.left;
        const rotate = this.getRotateRelativeSlide(nextSize, elementModel?.props?.style?.rotate);
        if (rotate % 90 === 0) {
            // 旋转90°的倍数时要单独处理，防止拉伸某一方向出现除数为0的情况。
            this.adsorptLeftRightRotate90(modes, rotate, leftchange, offsetFrame);
        } else {
            const rad = angleToRadian(rotate);
            if (offsetFrame.width === 0) {
                // 表明旋转后拉伸的是高度
                let height = leftchange / Math.abs(Math.sin(rad));
                const topchange = -leftchange / Math.tan(rad);
                if (modes === MODES.RIGHT) {
                    height *= -1;
                }
                this.calOffsetFrameHeightRotate(offsetFrame, height, topchange, rad);
            } else {
                let width = leftchange / Math.abs(Math.cos(rad));
                if (modes === MODES.RIGHT) {
                    width *= -1;
                }
                this.calOffsetFrameWidthRotate(offsetFrame, width, leftchange, rad);
            }
        }
    }

    /**
    * 处理当元素旋转90°的倍数时，吸附左右对齐线需要调整的位置
    * @param modes 拉伸的点
    * @param rotate 旋转的角度
    * @param leftchange 距离对齐线的距离
    * @param offsetFrame 元素样式的变更信息
    */
    private static adsorptLeftRightRotate90(
        modes: string, rotate: number, leftchange: number,
        offsetFrame: IOffsetFrame,
    ) {
        if (rotate === 180) {
            this.calOffsetFrameWidth(offsetFrame, leftchange);
        } else {
            // 当rotate为90或者270时，改变的是形状的高度
            if (modes === MODES.LEFT) {
                offsetFrame.height -= leftchange;
                offsetFrame.top += leftchange / 2;
            } else {
                offsetFrame.height += leftchange;
                offsetFrame.top -= leftchange / 2;
            }
            offsetFrame.left += leftchange / 2;
        }
    }

    /**
    * 处理组合中的元素，自身无旋转，但parent有旋转，吸附左右对齐线
    * @param adsorptionParams
    */
    private static adsorptLeftRightNoRotateRelativeGroup(adsorptionParams) {
        const { coordinate, nextSize, offsetFrame, outer } = adsorptionParams;
        const { rotate } = nextSize;
        const rad = angleToRadian(rotate);
        const leftchange = coordinate.left  - outer.left;
        if (offsetFrame.height === 0) {
            const width = (rotate === 90 || rotate === 270) ? leftchange : leftchange / Math.cos(rad);
            this.calOffsetFrameWidth(offsetFrame, width);
        } else {
            const height = rotate === 180 ? leftchange : leftchange / Math.sin(rad);
            this.calOffsetFrameHeight(offsetFrame, height);
        }
    }

    /**
    * 计算无旋转时的offsetFrame width和left的变换
    * @param offsetFrame 元素样式的变更信息
    * @param width 需要改变的值
    */
    private static calOffsetFrameWidth(offsetFrame, width: number): void {
    // 如果left几乎没改变，表明只改变width
        if (Math.abs(offsetFrame.left) < 0.01) {
            offsetFrame.width += width;
        } else {
            offsetFrame.width -= width;
            offsetFrame.left += width;
        }
    }

    /**
    * 计算无旋转时的offsetFrame height和top的变换
    * @param offsetFrame 元素样式的变更信息
    * @param height 需要改变的值
    */
    private static calOffsetFrameHeight(offsetFrame, height: number): void {
    // 如果top几乎没改变，表明只改变height
        if (Math.abs(offsetFrame.top) < 0.01) {
            offsetFrame.height -= height;
        } else {
            offsetFrame.height += height;
            offsetFrame.top -= height;
        }
    }

    /**
    * 计算有旋转的offsetFrame width和left的变换
    * @param offsetFrame 元素样式的变更信息
    * @param width 需要改变的宽度值
    * @param leftchange 距离对齐线的距离
    * @param rad 角度
    */
    private static calOffsetFrameWidthRotate(offsetFrame: IOffsetFrame, width, leftchange, rad): void {
        offsetFrame.width -= width;
        offsetFrame.top += leftchange * Math.tan(rad) / 2;
        offsetFrame.left += (width + leftchange) / 2;
    }

    /**
    * 计算有旋转的offsetFrame height和top的变换
    * @param offsetFrame 元素样式的变更信息
    * @param height 需要改变的高度值
    * @param topchange 距离对齐线的距离
    * @param rad 角度
    */
    private static calOffsetFrameHeightRotate(offsetFrame: IOffsetFrame, height, topchange, rad): void {
        offsetFrame.height -= height;
        offsetFrame.top += (height + topchange) / 2;
        offsetFrame.left -= topchange *  Math.tan(rad) / 2;
    }

    /**
     * 计算组合子元素旋转角度
     * @param nextSize 缩放后变化的size
     */
    private static getRotateForGroupChildren(nextSize): number {
        if (has(nextSize, 'rotate')) {
            return nextSize.rotate;
        }
        return NO_ROTATE;
    }

    /**
    * 获取旋转角度，如果是组合的子元素，就返回元素相对slide的旋转角度
    * @param nextSize 缩放后变化的size
    * @param rotate 旋转角度
    * @returns 相对slide的旋转角度
    */
    private static getRotateRelativeSlide(nextSize, rotate: number): number {
        const groupChildrenRotate = this.getRotateForGroupChildren(nextSize);
        return this.hasRotate(groupChildrenRotate) ? groupChildrenRotate : rotate;
    }

    // 吸附到对齐线上去
    private static adsorptionByLines(lines, width: number, height: number): ILines {
    // coordinate的left或top是指 如果形状未旋转则为形状紧贴对齐线时的left或top；
    // 如果形状有旋转则为能包裹旋转的形状的形状紧贴对齐线时的left或top
        let left;
        let top;
        for (const iterator in lines) {
            if ({}.hasOwnProperty.call(lines, iterator)) {
                const adsorptionDistance = {
                    [COORDINATE.LEFT_TOP]: {
                        left: lines[iterator][LINES_DISTSNCE_INDEX],
                    },
                    [COORDINATE.LEFT_MID]: {
                        left: lines[iterator][LINES_DISTSNCE_INDEX] - (width / 2),
                    },
                    [COORDINATE.LEFT_BOT]: {
                        left: lines[iterator][LINES_DISTSNCE_INDEX] - width,
                    },
                    [COORDINATE.TOP_LEFT]: {
                        top: lines[iterator][LINES_DISTSNCE_INDEX],
                    },
                    [COORDINATE.TOP_MIDD]: {
                        top: lines[iterator][LINES_DISTSNCE_INDEX] - (height / 2),
                    },
                    [COORDINATE.TOP_RIGT]: {
                        top: lines[iterator][LINES_DISTSNCE_INDEX] - height,
                    },
                }[lines[iterator][LINES_MODE_INDEX]];
                if (adsorptionDistance?.left) {
                    left = adsorptionDistance?.left;
                }
                if (adsorptionDistance?.top) {
                    top = adsorptionDistance?.top;
                }
            }
        }
        return { left, top };
    }

    private static switchModeLeft4m(e, outer, mode, coordinate, original, offsetFrame, scale, multiRect) {
        const leftchange = coordinate.left  - multiRect.left + outer.left - original.left;
        switch (mode) {
            case MODES.MOVE:
                offsetFrame.left = coordinate.left  - multiRect.left + outer.left - original.left;
                break;
            case MODES.LEFT:
            case MODES.TOP_LEFT:
            case MODES.BOTTOM_LEFT:
                offsetFrame.width = -1 * leftchange;
                offsetFrame.left = leftchange;
                break;
            case MODES.RIGHT:
            case MODES.TOP_RIGHT:
            case MODES.BOTTOM_RIGHT:
                offsetFrame.width += leftchange;
                break;
            default:
                break;
        }

        setModeLeftOffsetFrameWithShiftKey({
            shiftKey: e?.shiftKey,
            mode,
            offsetFrame,
            scale,
        });
    }

    private static switchModeTop4m(e, outer, mode, coordinate, original, offsetFrame, scale, multiRect) {
        const topchange = coordinate.top - multiRect.top + outer.top - original.top;
        switch (mode) {
            case MODES.MOVE:
                offsetFrame.top = coordinate.top - multiRect.top + outer.top - original.top;
                break;
            case MODES.TOP:
            case MODES.TOP_LEFT:
            case MODES.TOP_RIGHT:
                offsetFrame.height = -1 * topchange;
                offsetFrame.top = topchange;
                break;
            case MODES.BOTTOM:
            case MODES.BOTTOM_LEFT:
            case MODES.BOTTOM_RIGHT:
                offsetFrame.height += topchange;
                break;
            default:
                break;
        }

        setModeTopOffsetFrameWithShiftKey({
            shiftKey: e?.shiftKey,
            mode,
            offsetFrame,
            scale,
        });
    }
}
